import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, CheckCircle2, Swords, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [focused, setFocused] = useState(null);

  const emailValid = EMAIL_RE.test(email.trim());
  const passValid = password.length >= 8;
  const canSubmit = emailValid && passValid;

  const validate = () => {
    const e = {};
    if (!emailValid) e.email = 'Enter a valid email';
    if (!passValid) e.password = 'Min 8 characters';
    setFieldErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate('/app/feed');
    } catch (err) {
      setError(err.message || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  const borderColor = (field) => {
    if (fieldErrors[field]) return '#F87171';
    if (focused === field) return '#0EA5E9';
    if (field === 'email' && emailValid && email.length > 0) return 'rgba(14,165,233,0.6)';
    if (field === 'password' && passValid) return 'rgba(14,165,233,0.6)';
    return 'rgba(56,189,248,0.2)';
  };

  return (
    <div className="auth-root">
      <div className="auth-blobs">
        <div className="auth-blob auth-blob-1" />
        <div className="auth-blob auth-blob-2" />
        <div className="auth-blob auth-blob-3" />
      </div>

      <div className="auth-card-wrap">
        {/* Back to home */}
        <Link to="/" className="auth-back-link">
          <ArrowLeft size={16} />
          <span>Home</span>
        </Link>

        {/* Header */}
        <div className="auth-header">
          <Link to="/" className="auth-logo-link">
            <img src="/rivalogo.png" alt="RivalCam" className="auth-logo" />
          </Link>
          <h1 className="auth-title">Return to the rivalry.</h1>
          <p className="auth-subtitle">Sign in and keep the hype rolling.</p>
        </div>

        {/* Card */}
        <div className="auth-card">
          <div className="auth-card-stripe" />
          <form onSubmit={handleSubmit} className="auth-form" noValidate>
            {/* Email */}
            <div className="auth-field">
              <label className="auth-label">Email</label>
              <div className="auth-input-wrap" style={{ borderColor: borderColor('email') }}>
                <Mail size={18} className="auth-input-icon" color={focused === 'email' ? '#38BDF8' : '#64748B'} />
                <input
                  type="email"
                  className="auth-input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setFieldErrors(prev => ({...prev, email: ''})); }}
                  onFocus={() => setFocused('email')}
                  onBlur={() => setFocused(null)}
                  autoComplete="email"
                />
                {emailValid && email.length > 0 && (
                  <CheckCircle2 size={18} color="#0EA5E9" className="auth-input-trail" />
                )}
              </div>
              {fieldErrors.email && <span className="auth-field-error">{fieldErrors.email}</span>}
            </div>

            {/* Password */}
            <div className="auth-field">
              <label className="auth-label">Password</label>
              <div className="auth-input-wrap" style={{ borderColor: borderColor('password') }}>
                <Lock size={18} className="auth-input-icon" color={focused === 'password' ? '#38BDF8' : '#64748B'} />
                <input
                  type={showPass ? 'text' : 'password'}
                  className="auth-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setFieldErrors(prev => ({...prev, password: ''})); }}
                  onFocus={() => setFocused('password')}
                  onBlur={() => setFocused(null)}
                  autoComplete="current-password"
                />
                <button type="button" className="auth-eye" onClick={() => setShowPass(s => !s)}>
                  {showPass ? <EyeOff size={18} color="#64748B" /> : <Eye size={18} color="#64748B" />}
                </button>
              </div>
              {fieldErrors.password && <span className="auth-field-error">{fieldErrors.password}</span>}
            </div>

            {/* Meta row */}
            <div className="auth-meta">
              <span />
              <Link to="/forgot-password" className="auth-forgot">Forgot password?</Link>
            </div>

            {error && <div className="auth-server-error">{error}</div>}

            <button type="submit" className="auth-btn" disabled={!canSubmit || loading}>
              {loading ? (
                <span className="auth-spinner" />
              ) : (
                <>
                  <span>Sign in</span>
                  {canSubmit && <ArrowRight size={18} />}
                </>
              )}
            </button>

            {/* Google sign-in — re-enable when ready
            <div className="auth-divider">
              <span />
              <span className="auth-divider-text">or continue with</span>
              <span />
            </div>

            <a
              href={`https://accounts.google.com/o/oauth2/v2/auth?client_id=337528326009-b3bt1sk5vsgfqpqbvv7r46ua0r1k0hfb.apps.googleusercontent.com&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/google/callback')}&response_type=token&scope=profile%20email%20openid`}
              className="auth-social-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#EA4335" d="M5.27 9.76A7.08 7.08 0 0 1 12 4.9c1.84 0 3.5.67 4.79 1.77l3.56-3.56A12 12 0 0 0 12 0 12 12 0 0 0 1.22 6.69l4.05 3.07Z"/>
                <path fill="#34A853" d="M16.04 18.01A7.08 7.08 0 0 1 12 19.1a7.08 7.08 0 0 1-6.73-4.86l-4.06 3.12A12 12 0 0 0 12 24a12 12 0 0 0 8.09-3.14l-4.05-2.85Z"/>
                <path fill="#4A90D9" d="M20.09 20.86A12 12 0 0 0 24 12c0-.72-.07-1.42-.2-2.1H12v4.51h6.84a5.83 5.83 0 0 1-2.8 3.6l4.05 2.85Z"/>
                <path fill="#FBBC05" d="M5.27 14.24A7.08 7.08 0 0 1 4.9 12c0-.78.14-1.54.38-2.24L1.22 6.69A12 12 0 0 0 0 12c0 1.92.46 3.73 1.22 5.36l4.05-3.12Z"/>
              </svg>
              <span>Continue with Google</span>
            </a>
            */}
          </form>
        </div>

        <p className="auth-footer-text">
          New here?{' '}
          <Link to="/signup" className="auth-footer-link">Create an account</Link>
        </p>

        <p className="auth-policy-note">
          By signing in you agree to our{' '}
          <a href="https://meanc.net/info/terms" target="_blank" rel="noreferrer" className="auth-policy-link">Terms</a>
          {' '}and{' '}
          <a href="https://meanc.net/info/community-guidelines" target="_blank" rel="noreferrer" className="auth-policy-link">Community Guidelines</a>.
        </p>
      </div>
    </div>
  );
}
